"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Play } from "lucide-react";

interface VideoFuncionalidad {
    ruta: string;
    poster: string;
    duracionSegundos: number;
    ancho: number;
    alto: number;
}

interface PropiedadesVideoExplicativo {
    video?: VideoFuncionalidad;
    etiqueta: string;
    transcripcion: string;
}

// mm:ss — un cronómetro, no un calendario, así que no hace falta ninguna
// librería de fechas para formatear la duración de un vídeo de segundos.
function formatearDuracion(duracionSegundos: number): string {
    const totalSegundos = Math.round(duracionSegundos);
    const minutos = Math.floor(totalSegundos / 60);
    const segundos = totalSegundos % 60;
    return `${minutos}:${String(segundos).padStart(2, "0")}`;
}

// El .vtt de subtítulos no es un sexto campo de metadata: se deriva por
// convención del mismo nombre que el .mp4 (ver navegacionFuncionalidades.ts).
function rutaSubtitulos(rutaVideo: string): string {
    return rutaVideo.replace(/\.mp4$/, ".vtt");
}

export default function VideoExplicativo({ video, etiqueta, transcripcion }: PropiedadesVideoExplicativo) {
    const [reproduciendo, setReproduciendo] = useState(false);
    const referenciaVideo = useRef<HTMLVideoElement>(null);

    // Arrancar la reproducción y activar los subtítulos vive en un efecto
    // sobre `reproduciendo`, no en el propio manejador de clic (ni en el
    // guard de `!video` de abajo — un hook no puede quedar detrás de un
    // return condicional). Hacerlo en el manejador dependía de que React ya
    // hubiera confirmado `controls={true}` en el mismo tick del
    // `setReproduciendo` — no lo había, un <video> sin controles no es
    // enfocable, y el `.focus()` síncrono no hacía nada mientras el botón
    // Play (que sí tenía el foco) se desmontaba en ese mismo render: el
    // foco caía en <body> (bug real, detectado con Playwright contra el
    // build de producción en las nueve páginas de capacidad). El efecto
    // corre DESPUÉS del commit, así que para entonces el elemento ya es
    // enfocable de verdad.
    useEffect(() => {
        if (!reproduciendo) return;

        const elemento = referenciaVideo.current;
        if (!elemento) return;

        elemento.play()?.catch(() => undefined);
        elemento.focus();

        // Los subtítulos se activan acá, por script, en vez de con el
        // atributo `default` del <track>: ese atributo hace que el
        // navegador baje el .vtt en cuanto el <video> se monta — sin
        // importar `preload` — así que dejarlo puesto rompía la promesa de
        // "cero bytes hasta el clic". Con `default` fuera, el modo por
        // defecto de la pista es "disabled" (no se pide el archivo); acá se
        // pasa a "showing" recién cuando el usuario ya hizo clic.
        const pistaSubtitulos = elemento.textTracks[0];
        if (pistaSubtitulos) pistaSubtitulos.mode = "showing";
    }, [reproduciendo]);

    // Sin vídeo declarado en FUNCIONALIDADES_NAV, la sección no existe: nada
    // de placeholder ni copy de "próximamente" (D-07, UI-SPEC V1 × empty).
    if (!video) return null;

    const reproducir = () => {
        setReproduciendo(true);
    };

    return (
        <section data-section="video" className="max-w-xl mx-auto lg:max-w-none px-6 lg:px-0">
            <div
                className="relative overflow-hidden rounded-3xl border-2 border-ink shadow-ds-3 bg-ink"
                style={{ aspectRatio: `${video.ancho} / ${video.alto}` }}
            >
                {/*
                 * El vídeo se renderiza SIEMPRE (SSR incluido) para que Google
                 * lo encuentre en el HTML servido — un VideoObject declarado
                 * sobre un <video> ausente invalida el structured data. El
                 * facade de clic-para-reproducir sigue existiendo, pero ahora
                 * como overlay encima de este elemento, no como su sustituto.
                 * `preload="none"` mantiene el vídeo en cero bytes de red
                 * hasta el clic; el <track> de abajo, sin `default`, hace lo
                 * mismo con los subtítulos — con `default` puesto el
                 * navegador baja el .vtt en cuanto el <video> se monta, sin
                 * mirar `preload` para nada (ver el efecto más arriba, que
                 * activa la pista por script recién tras el clic). Sin
                 * `poster` en el <video> mismo: el overlay de abajo (una
                 * <Image> optimizada por next/image) ya hace de póster —
                 * declarar también `poster` aquí forzaría al navegador a
                 * descargar el JPG crudo además de la versión optimizada que
                 * next/image ya sirve. `tabIndex={-1}` deja el elemento
                 * enfocable por script (lo necesita el efecto de arriba)
                 * sin meterlo en el orden de tabulación mientras el botón
                 * Play sigue siendo el único control accesible.
                 */}
                <video
                    ref={referenciaVideo}
                    className="block w-full h-full"
                    width={video.ancho}
                    height={video.alto}
                    controls={reproduciendo}
                    playsInline
                    preload="none"
                    tabIndex={-1}
                >
                    <source src={video.ruta} type="video/mp4" />
                    <track
                        kind="captions"
                        src={rutaSubtitulos(video.ruta)}
                        srcLang="es"
                        label="Español"
                    />
                </video>

                {!reproduciendo && (
                    <>
                        <Image
                            src={video.poster}
                            alt={`Vista previa del vídeo: ${etiqueta}`}
                            fill
                            sizes="(max-width: 1024px) 100vw, 768px"
                            preload
                            className="object-cover"
                        />
                        <button
                            type="button"
                            onClick={reproducir}
                            aria-label={`Reproducir vídeo: ${etiqueta}`}
                            className="absolute inset-0 m-auto w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-blue border-2 border-ink text-on-accent-blue shadow-[4px_4px_0px_0px_var(--shadow-color)] flex items-center justify-center transition-transform hover:scale-105"
                        >
                            <Play aria-hidden="true" className="w-6 h-6 md:w-8 md:h-8 fill-current" />
                        </button>
                        <span className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 bg-ink/80 text-canvas rounded-full px-ds-2 py-ds-1 text-ds-200 font-medium font-sans">
                            {formatearDuracion(video.duracionSegundos)}
                        </span>
                    </>
                )}
            </div>

            <details className="mt-ds-6">
                <summary className="cursor-pointer text-ds-200 font-bold font-display text-ink">
                    Transcripción del vídeo
                </summary>
                <div className="mt-ds-4 text-ds-400 font-medium text-ink/70 leading-relaxed">
                    <p>{transcripcion}</p>
                </div>
            </details>
        </section>
    );
}
